import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "react-query";
import toast from "react-hot-toast";
import { Plus } from "lucide-react";
import MultiSelectDropdown from "./MultiSelectDropdown";
import LoadingSpinner from "./LoadingSpinner";
import RoadmapTimeline from "./RoadmapTimeline";
import RoadmapItemEditor from "./RoadmapItemEditor";
import { roadmapStatuses } from "../utils/roadmapStatus";
import { quarterDates, roadmapQuarters } from "../utils/roadmapQuarters";
import { apiService, extractApiError } from "../services/api";
import type {
  RoadmapItem,
  RoadmapItemCreate,
  RoadmapItemUpdate,
  RoadmapTeam,
  RoadmapTopic,
} from "../types";

interface RoadmapBoardProps {
  teams: RoadmapTeam[];
  topics: RoadmapTopic[];
}

export default function RoadmapBoard({ teams, topics }: RoadmapBoardProps) {
  const [params, setParams] = useSearchParams();
  const client = useQueryClient();
  const itemId = params.get("item") ?? "";
  const teamParam = params.get("team") ?? "";
  const team =
    teams.find((candidate) => candidate.slug === teamParam) ??
    teams.find((candidate) => candidate.id === teamParam);
  const teamId = team?.id ?? teamParam;
  const yearParam = params.get("year");
  const year =
    yearParam === null ? new Date().getFullYear() : Number(yearParam);
  const validYear = Number.isInteger(year) && year >= 1 && year <= 9999;
  useEffect(() => {
    if (itemId) return;
    const defaultTeam = !params.has("team")
      ? teams.find(
          (candidate) => candidate.name.trim().toLowerCase() === "devx",
        )
      : undefined;
    if (
      defaultTeam ||
      (team && (yearParam === null || teamParam !== team.slug))
    ) {
      const next = new URLSearchParams(params);
      if (defaultTeam) next.set("team", defaultTeam.slug);
      else if (team) next.set("team", team.slug);
      if (yearParam === null) {
        next.set("year", String(year));
        if (!next.has("quarter"))
          next.set(
            "quarter",
            String(Math.floor(new Date().getMonth() / 3) + 1),
          );
      }
      setParams(next, { replace: true });
    }
  }, [itemId, teams, team, teamParam, yearParam, year, params, setParams]);
  const teamTopics = topics.filter((topic) => topic.teamId === teamId);
  const selectedTopics = params
    .getAll("topic")
    .filter((id) => teamTopics.some((topic) => topic.id === id));
  const selectedStatuses = params
    .getAll("status")
    .filter((status) => Object.keys(roadmapStatuses).includes(status));
  const selectedQuarters = roadmapQuarters.filter((quarter) =>
    params.getAll("quarter").includes(quarter),
  );
  const quarterRanges: { start: number; end: number }[] = [];
  for (const quarter of selectedQuarters.length
    ? selectedQuarters
    : roadmapQuarters) {
    const value = Number(quarter);
    const previous = quarterRanges[quarterRanges.length - 1];
    if (previous && previous.end + 1 === value) previous.end = value;
    else quarterRanges.push({ start: value, end: value });
  }
  const overlapsQuarters = (item: RoadmapItem, selectedYear: number) =>
    quarterRanges.some((range) => {
      const dates = quarterDates(selectedYear, range.start, range.end);
      return item.startDate <= dates.endDate && item.endDate >= dates.startDate;
    });
  const [adding, setAdding] = useState(false);
  const [savedDraft, setSavedDraft] = useState<RoadmapItem | null>(null);
  const itemQuery = useQuery(
    ["roadmap-item", itemId],
    async () => {
      const { data } = await apiService.getRoadmapItem(itemId);
      if (!data.success || !data.data)
        throw new Error(data.error || "Failed to load roadmap item");
      return data.data;
    },
    { enabled: Boolean(itemId), retry: false, staleTime: 30000 },
  );
  const linkedItem = itemId ? itemQuery.data : undefined;
  const linkedTeam = teams.find((candidate) =>
    topics.some(
      (topic) =>
        topic.id === linkedItem?.topicId && topic.teamId === candidate.id,
    ),
  );
  const editingItem = linkedTeam?.id === teamId ? (linkedItem ?? null) : null;
  useEffect(() => {
    if (!linkedItem || !linkedTeam) return;
    if (teamParam !== linkedTeam.slug || yearParam === null) {
      const next = new URLSearchParams(params);
      next.set("team", linkedTeam.slug);
      if (yearParam === null)
        next.set("year", linkedItem.startDate.slice(0, 4));
      setParams(next, { replace: true });
    }
  }, [linkedItem, linkedTeam, teamParam, yearParam, params, setParams]);
  const closeItem = () => {
    const next = new URLSearchParams(params);
    next.delete("item");
    setParams(next);
    setAdding(false);
    setSavedDraft(null);
  };
  useEffect(() => {
    setAdding(false);
    setSavedDraft(null);
  }, [teamId, itemId]);
  const showSavedItem = (item: RoadmapItem, keepOpen = false) => {
    client.invalidateQueries("roadmap-items");
    const next = new URLSearchParams(params);
    if (!keepOpen) next.delete("item");
    const startYear = Number(item.startDate.slice(0, 4));
    const endYear = Number(item.endDate.slice(0, 4));
    if (year < startYear || year > endYear) next.set("year", String(startYear));
    if (selectedTopics.length && !selectedTopics.includes(item.topicId))
      next.delete("topic");
    if (selectedStatuses.length && !selectedStatuses.includes(item.status))
      next.delete("status");
    if (
      selectedQuarters.length &&
      !overlapsQuarters(
        item,
        year < startYear || year > endYear ? startYear : year,
      )
    )
      next.delete("quarter");
    setParams(next, { replace: keepOpen });
    setAdding(false);
  };
  const itemsQuery = useQuery(
    ["roadmap-items", teamId, year],
    async () => {
      const { data } = await apiService.getRoadmapItems(teamId, year);
      if (!data.success || !data.data)
        throw new Error(data.error || "Failed to load roadmap items");
      return data.data.items;
    },
    { enabled: Boolean(team) && validYear },
  );
  const createItem = useMutation(
    async (payload: RoadmapItemCreate) => {
      const { data } = await apiService.createRoadmapItem(payload);
      if (!data.success || !data.data)
        throw new Error(data.error || "Failed to create roadmap item");
      return data.data;
    },
    {
      onSuccess: (item) => {
        showSavedItem(item);
        toast.success("Roadmap item added");
      },
      onError: (error) => {
        toast.error(extractApiError(error));
      },
    },
  );
  const updateItem = useMutation(
    async ({
      id,
      payload,
    }: {
      id: string;
      payload: RoadmapItemUpdate;
      titleOnly?: boolean;
      criterionOnly?: boolean;
    }) => {
      const { data } = await apiService.updateRoadmapItem(id, payload);
      if (!data.success || !data.data)
        throw new Error(data.error || "Failed to update roadmap item");
      return data.data;
    },
    {
      onSuccess: (item, variables) => {
        client.setQueryData(["roadmap-item", item.id], item);
        if (variables.titleOnly || variables.criterionOnly) {
          client.invalidateQueries("roadmap-items");
        } else {
          showSavedItem(item, editingItem?.id === item.id);
          setSavedDraft(item);
        }
        toast.success("Roadmap item updated");
      },
      onError: (error) => {
        toast.error(extractApiError(error));
      },
    },
  );
  const deleteItem = useMutation(
    async (id: string) => {
      const { data } = await apiService.deleteRoadmapItem(id);
      if (!data.success)
        throw new Error(data.error || "Failed to delete roadmap item");
    },
    {
      onSuccess: (_response, id) => {
        client.invalidateQueries("roadmap-items");
        client.removeQueries(["roadmap-item", id], { exact: true });
        if (itemId === id) closeItem();
        toast.success("Roadmap item deleted");
      },
      onError: (error) => {
        toast.error(extractApiError(error));
      },
    },
  );
  const busy =
    createItem.isLoading || updateItem.isLoading || deleteItem.isLoading;
  const items = (itemsQuery.data ?? []).filter(
    (item) =>
      (!selectedStatuses.length || selectedStatuses.includes(item.status)) &&
      overlapsQuarters(item, year),
  );
  const lanes = teamTopics.filter((topic) =>
    selectedTopics.length
      ? selectedTopics.includes(topic.id)
      : items.some((item) => item.topicId === topic.id),
  );
  const updateSelection = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (value || key === "year" || key === "team")
      next.set(
        key,
        key === "team"
          ? (teams.find((candidate) => candidate.id === value)?.slug ?? value)
          : value,
      );
    else next.delete(key);
    if (key === "team") {
      next.delete("topic");
      next.delete("item");
      if (!next.has("year")) next.set("year", String(new Date().getFullYear()));
      if (value && !next.has("quarter"))
        next.set("quarter", String(Math.floor(new Date().getMonth() / 3) + 1));
      setAdding(false);
    }
    setParams(next);
  };

  return (
    <div className="space-y-4">
      <div className="card flex flex-wrap items-end gap-4">
        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
          Team
          <select
            className="input mt-1"
            value={teamId}
            disabled={busy}
            onChange={(event) => updateSelection("team", event.target.value)}
          >
            <option value="">Select team</option>
            {teamId && !team && (
              <option value={teamId} disabled>
                Unavailable team
              </option>
            )}
            {teams.map((candidate) => (
              <option key={candidate.id} value={candidate.id}>
                {candidate.name}
              </option>
            ))}
          </select>
        </label>
        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
          Year
          <input
            className="input mt-1 w-32"
            type="number"
            min={1}
            max={9999}
            value={validYear ? year : (yearParam ?? "")}
            disabled={!team || busy}
            onChange={(event) => updateSelection("year", event.target.value)}
          />
        </label>
        <fieldset className="space-y-1">
          <legend className="text-sm font-medium text-gray-700 dark:text-gray-300">
            Quarters
          </legend>
          <MultiSelectDropdown
            placeholder="All quarters"
            disabled={!team || busy}
            options={roadmapQuarters.map((id) => ({ id, label: `Q${id}` }))}
            selected={selectedQuarters}
            onToggle={(id, selected) => {
              const next = new URLSearchParams(params);
              next.delete("quarter");
              roadmapQuarters
                .filter((quarter) =>
                  quarter === id
                    ? selected
                    : selectedQuarters.includes(quarter),
                )
                .forEach((quarter) => next.append("quarter", quarter));
              setParams(next);
            }}
          />
        </fieldset>
        <fieldset className="space-y-1">
          <legend className="text-sm font-medium text-gray-700 dark:text-gray-300">
            Topics
          </legend>
          <MultiSelectDropdown
            placeholder="All topics"
            disabled={!team || busy}
            options={teamTopics.map((topic) => ({
              id: topic.id,
              label: topic.name,
            }))}
            selected={selectedTopics}
            onToggle={(id, selected) => {
              const next = new URLSearchParams(params);
              next.delete("topic");
              const ids = selected
                ? [...selectedTopics, id]
                : selectedTopics.filter((topicId) => topicId !== id);
              ids.forEach((topicId) => next.append("topic", topicId));
              setParams(next);
            }}
          />
        </fieldset>
        <fieldset className="space-y-1">
          <legend className="text-sm font-medium text-gray-700 dark:text-gray-300">
            Status
          </legend>
          <MultiSelectDropdown
            placeholder="All statuses"
            disabled={!team || busy}
            options={Object.entries(roadmapStatuses).map(([id, status]) => ({
              id,
              label: status.label,
            }))}
            selected={selectedStatuses}
            onToggle={(id, selected) => {
              const next = new URLSearchParams(params);
              next.delete("status");
              const statuses = selected
                ? [...selectedStatuses, id]
                : selectedStatuses.filter((status) => status !== id);
              statuses.forEach((status) => next.append("status", status));
              setParams(next);
            }}
          />
        </fieldset>
      </div>
      {teamId && !team && (
        <p role="alert" className="text-red-600 dark:text-red-400">
          This team is unavailable. Select another team.
        </p>
      )}
      {!teamId && (
        <p className="card text-gray-600 dark:text-gray-300">
          Select a team to view its roadmap.
        </p>
      )}
      {team && !validYear && (
        <p role="alert" className="text-red-600 dark:text-red-400">
          Enter a year from 1 to 9999.
        </p>
      )}
      {itemId && itemQuery.isLoading && (
        <p role="status" className="card">
          Loading roadmap item...
        </p>
      )}
      {itemId && (itemQuery.isError || (linkedItem && !linkedTeam)) && (
        <div role="alert" className="card space-y-3">
          <p>
            This roadmap item is unavailable or could not be loaded. It may have
            been deleted.
          </p>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => itemQuery.refetch()}
          >
            Retry item
          </button>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={closeItem}
          >
            Dismiss item
          </button>
        </div>
      )}
      {team && validYear && (
        <>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              {team.name} - {year}
            </h2>
            {team.canManageTopics && (
              <button
                type="button"
                className="btn btn-primary gap-2"
                disabled={!teamTopics.length || busy}
                onClick={() => {
                  if (itemId) closeItem();
                  setAdding((value) => !value);
                }}
              >
                <Plus className="h-4 w-4" /> Add a roadmap item
              </button>
            )}
          </div>
          {team.canManageTopics && !teamTopics.length && (
            <p className="text-sm text-gray-600 dark:text-gray-300">
              A team creator must add a topic in Manage before items can be
              added.
            </p>
          )}
          {(adding || editingItem) && (
            <RoadmapItemEditor
              itemId={editingItem?.id}
              teamOwnerEmail={team.createdByEmail}
              key={editingItem?.id ?? "new"}
              initial={
                editingItem ?? {
                  title: "",
                  description: "",
                  topicId: "",
                  startDate: "",
                  endDate: "",
                }
              }
              savedDraft={
                savedDraft?.id === editingItem?.id ? savedDraft : null
              }
              editing={Boolean(editingItem)}
              createdByEmail={editingItem?.createdByEmail}
              createdByName={editingItem?.createdByName}
              createdAt={editingItem?.createdAt}
              readOnly={!team.canManageTopics}
              topics={teamTopics}
              busy={busy}
              onToggleCriterion={(id, completed) => {
                if (editingItem)
                  updateItem.mutate({
                    id: editingItem.id,
                    payload: { criterion: { id, completed } },
                    criterionOnly: true,
                  });
              }}
              onSaveTitle={(title) => {
                if (editingItem)
                  updateItem.mutate({
                    id: editingItem.id,
                    payload: { title },
                    titleOnly: true,
                  });
              }}
              onSave={(payload) => {
                if (editingItem)
                  updateItem.mutate({ id: editingItem.id, payload });
                else createItem.mutate(payload);
              }}
              onClose={closeItem}
              onDelete={
                editingItem && team.canManageTopics
                  ? () => {
                      if (
                        window.confirm(
                          `Delete roadmap item "${editingItem.title}"?`,
                        )
                      )
                        deleteItem.mutate(editingItem.id);
                    }
                  : undefined
              }
            />
          )}
          {itemsQuery.isLoading ? (
            <LoadingSpinner />
          ) : itemsQuery.isError ? (
            <div role="alert" className="card space-y-2">
              <p>Unable to load roadmap items.</p>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => itemsQuery.refetch()}
              >
                Retry items
              </button>
            </div>
          ) : lanes.length === 0 ? (
            <p className="card text-gray-600 dark:text-gray-300">
              No roadmap items for this selection.
            </p>
          ) : (
            <>
              {!items.some((item) =>
                lanes.some((topic) => topic.id === item.topicId),
              ) && (
                <p className="text-sm text-gray-600 dark:text-gray-300">
                  No roadmap items for this selection.
                </p>
              )}
              {selectedQuarters.length > 0 && team.canManageTopics && (
                <p className="text-sm text-gray-600 dark:text-gray-300">
                  Clear the quarter filter to drag or resize projects. Project
                  details remain editable.
                </p>
              )}
              {quarterRanges.map((range) => (
                <RoadmapTimeline
                  key={`${year}-${range.start}-${range.end}`}
                  year={year}
                  startQuarter={range.start}
                  endQuarter={range.end}
                  topics={lanes}
                  items={items}
                  canEdit={team.canManageTopics && !selectedQuarters.length}
                  busy={busy}
                  onOpen={(item) => {
                    client.setQueryData(["roadmap-item", item.id], item);
                    const next = new URLSearchParams(params);
                    next.set("item", item.id);
                    setAdding(false);
                    setParams(next);
                  }}
                  onUpdate={(id, payload) => updateItem.mutate({ id, payload })}
                />
              ))}
            </>
          )}
        </>
      )}
    </div>
  );
}
